// Pure helpers for the wholesale forecast-by-account grid: month formatting,
// lock-window checks, and subtotal/grand-total math. Kept dependency-free so
// they're trivial to unit test without mounting any React component.

export interface ForecastRow {
  customer: { id: string; name: string };
  pic_user: { id: string; name: string } | null;
  values: Record<string, number>;
}

export interface ForecastProduct {
  external_id: string;
  name: string;
  short_name?: string | null;
  // Wholesale-channel ASP for this product in the pinned plan; null/absent
  // when unpriced (scaffolds, missing ASP) — the product is then skipped by
  // the Revenue total rather than counted as zero.
  price?: number | null;
  rows: ForecastRow[];
}

// Grid rows label products by short_name when set — full names are long
// enough to always truncate in the 260px sticky column. The full name stays
// available in the row tooltip.
export function productLabel(product: { name: string; short_name?: string | null }): string {
  return product.short_name || product.name;
}

export interface CustomerInfo {
  id: string;
  name: string;
  pic_user: { id: string; name: string } | null;
}

// A product picked from the "+ Add product" search before it has any saved
// cells. Once a cell is saved for it, the server's `products` prop includes
// the real group and the scaffold is superseded (see mergeScaffolds).
export interface ScaffoldProduct {
  external_id: string;
  name: string;
  short_name?: string | null;
}

// A customer picked via "+ Add customer to {product}" before it has any
// saved cells for that product.
export interface ScaffoldRow {
  productExternalId: string;
  customerId: string;
}

// "2026-07-01" -> "07/26"
export function monthLabel(iso: string): string {
  const [year, month] = iso.split("-");
  return `${month}/${year.slice(2)}`;
}

// Months up to and including `lockedThroughIso` are read-only (ISO
// yyyy-mm-dd strings sort lexicographically, so string comparison suffices).
export function isLocked(monthIso: string, lockedThroughIso: string): boolean {
  return monthIso <= lockedThroughIso;
}

// Sum of a product's per-customer values for one month. A row missing the
// month key is treated as "no data" and skipped; if every row is missing it,
// the subtotal is null (renders as "—") rather than a misleading 0.
export function productSubtotal(product: ForecastProduct, month: string): number | null {
  let sum = 0;
  let any = false;
  for (const row of product.rows) {
    const value = row.values[month];
    if (value != null) {
      sum += value;
      any = true;
    }
  }
  return any ? sum : null;
}

export function grandTotal(products: ForecastProduct[], month: string): number | null {
  let sum = 0;
  let any = false;
  for (const product of products) {
    const value = productSubtotal(product, month);
    if (value != null) {
      sum += value;
      any = true;
    }
  }
  return any ? sum : null;
}

// Revenue for one month: Σ units × wholesale ASP over priced products.
// Unpriced products are skipped; null (renders "—") when nothing is priced,
// so a grid with no ASPs shows dashes instead of a fake ₫0.
export function revenueTotal(products: ForecastProduct[], month: string): number | null {
  let sum = 0;
  let any = false;
  for (const product of products) {
    if (product.price == null) continue;
    const units = productSubtotal(product, month);
    if (units != null) {
      sum += units * product.price;
      any = true;
    }
  }
  return any ? sum : null;
}

export function formatUnits(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return "—";
  return value.toLocaleString("en-US");
}

// True once a product (server-loaded or scaffolded) is already on the grid,
// used to keep the "+ Add product" search from offering a duplicate.
export function isProductInList(
  products: ForecastProduct[],
  scaffoldProducts: ScaffoldProduct[],
  externalId: string,
): boolean {
  return (
    products.some((p) => p.external_id === externalId) ||
    scaffoldProducts.some((p) => p.external_id === externalId)
  );
}

// Customers not yet on a given product's grid, for the "+ Add customer" picker.
export function availableCustomersForProduct(
  product: ForecastProduct,
  customers: CustomerInfo[],
): CustomerInfo[] {
  const existing = new Set(product.rows.map((r) => r.customer.id));
  return customers.filter((c) => !existing.has(c.id));
}

// Splits a customer list into the current user's own accounts (shown first)
// and everyone else's, for the "Your customers" / "Others" picker groups.
export function groupCustomersForPicker(
  customers: CustomerInfo[],
  currentUserId: string | null,
): { mine: CustomerInfo[]; others: CustomerInfo[] } {
  const mine: CustomerInfo[] = [];
  const others: CustomerInfo[] = [];
  for (const customer of customers) {
    const isMine = currentUserId != null && customer.pic_user?.id === currentUserId;
    (isMine ? mine : others).push(customer);
  }
  return { mine, others };
}

// Overlays client-side scaffold products/rows on top of the server-loaded
// `products`, so a freshly added product/customer renders immediately without
// a round trip. A scaffold is dropped automatically once the server prop
// already has the same product+customer (i.e. after the first cell save and
// partial reload) — see the `existing` checks below — so no double row
// appears once the server catches up.
export function mergeScaffolds(
  products: ForecastProduct[],
  scaffoldProducts: ScaffoldProduct[],
  scaffoldRows: ScaffoldRow[],
  customersById: Map<string, CustomerInfo>,
): ForecastProduct[] {
  const extraProducts = scaffoldProducts
    .filter((sp) => !products.some((p) => p.external_id === sp.external_id))
    .map(
      (sp): ForecastProduct => ({
        external_id: sp.external_id,
        name: sp.name,
        short_name: sp.short_name ?? null,
        rows: [],
      }),
    );

  return [...products, ...extraProducts].map((product) => {
    const existingCustomerIds = new Set(product.rows.map((r) => r.customer.id));
    const additions: ForecastRow[] = scaffoldRows
      .filter((sr) => sr.productExternalId === product.external_id && !existingCustomerIds.has(sr.customerId))
      .map((sr) => customersById.get(sr.customerId))
      .filter((customer): customer is CustomerInfo => customer != null)
      .map((customer) => ({
        customer: { id: customer.id, name: customer.name },
        pic_user: customer.pic_user,
        values: {},
      }));

    return additions.length === 0 ? product : { ...product, rows: [...product.rows, ...additions] };
  });
}
